import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import {
  approveCompanyRegistration,
  rejectCompanyRegistration,
  subscribeCompanyRegistrations,
} from '../../../api/company-registration.js';
import { CompanyRegistrationStatus } from '../../../db/enums/registration-status.js';

const dateFormatter = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Europe/Berlin',
});

// Inline card for a single registration with approve/reject actions
const RegistrationCard = stateful(
  ({
    reg,
  }: {
    reg: {
      _id: string;
      companyName?: string | null;
      contactFirstName?: string | null;
      contactLastName?: string | null;
      contactEmail?: string | null;
      contactPosition?: string | null;
      address?: string | null;
      sector?: string | null;
      website?: string | null;
      services?: string | null;
      openPositions?: string | null;
      participationReason?: string | null;
      status?: CompanyRegistrationStatus | null;
      submittedAt?: Date | null;
    };
  }) => {
    const [processing, setProcessing] = useState(false, [reg._id]);
    const [showRejectForm, setShowRejectForm] = useState(false, [reg._id]);
    const [rejectionReason, setRejectionReason] = useState('', [reg._id]);

    const isPending = reg.status === CompanyRegistrationStatus.PENDING;
    const isApproved = reg.status === CompanyRegistrationStatus.APPROVED;
    const isRejected = reg.status === CompanyRegistrationStatus.REJECTED;

    const handleApprove = async () => {
      if (processing) return;
      setProcessing(true);
      try {
        await guardDialog(() => approveCompanyRegistration(reg._id)());
        showDialog({ content: () => m.admin_registration_status_approved() });
      } catch (e: any) {
        showDialog({ content: () => e?.message ?? 'Error' });
      } finally {
        setProcessing(false);
      }
    };

    const handleReject = async () => {
      if (processing || !rejectionReason.trim()) return;
      setProcessing(true);
      try {
        await guardDialog(() => rejectCompanyRegistration(reg._id, rejectionReason)());
        showDialog({ content: () => m.admin_registration_status_rejected() });
        setShowRejectForm(false);
      } catch (e: any) {
        showDialog({ content: () => e?.message ?? 'Error' });
      } finally {
        setProcessing(false);
      }
    };

    const statusBadge = () => {
      if (isApproved) return html`<span class="badge badge-success">${m.admin_registration_status_approved()}</span>`;
      if (isRejected) return html`<span class="badge badge-error">${m.admin_registration_status_rejected()}</span>`;
      return nothing;
    };

    const field = (label: string, value: string | null | undefined) =>
      value
        ? html`<div class="flex flex-col">
            <span class="text-xs font-semibold opacity-60">${label}</span>
            <span class="text-sm">${value}</span>
          </div>`
        : nothing;

    return html`
      <div class="card bg-base-100 shadow border border-base-300">
        <div class="card-body">
          <div class="flex justify-between items-start">
            <div class="text-xs opacity-50">
              ${reg.submittedAt ? dateFormatter.format(new Date(reg.submittedAt)) : ''}
            </div>
            ${statusBadge()}
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-2">
            <!-- Left column -->
            <div class="space-y-2">
              ${field(m.company_registration_companyName_label(), reg.companyName)}
              ${field(m.company_registration_address_label(), reg.address)}
              ${field(m.company_registration_sector_label(), reg.sector)}
              ${field(m.company_registration_website_label(), reg.website)}
              ${field(m.admin_registration_contact_person(), [reg.contactFirstName, reg.contactLastName, reg.contactPosition ? `(${reg.contactPosition})` : ''].filter(Boolean).join(' '))}
              ${field(m.company_registration_contactEmail_label(), reg.contactEmail)}
            </div>
            <!-- Right column -->
            <div class="space-y-2">
              ${field(m.company_registration_services_label(), reg.services)}
              ${field(m.company_registration_openPositions_label(), reg.openPositions)}
              ${field(m.company_registration_participationReason_label(), reg.participationReason)}
            </div>
          </div>

          ${isPending
            ? html`
                <div class="flex flex-wrap gap-2 mt-4 justify-end">
                  <button class="btn btn-success btn-sm" ?disabled=${processing} @click=${handleApprove}>
                    ${processing ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
                    ${m.admin_registration_approve_button()}
                  </button>
                  <button
                    class="btn btn-error btn-sm"
                    ?disabled=${processing}
                    @click=${() => setShowRejectForm(!showRejectForm)}
                  >
                    ${m.admin_registration_reject_button()}
                  </button>
                </div>
                ${showRejectForm
                  ? html`
                      <div class="mt-3 space-y-2">
                        <textarea
                          class="textarea textarea-bordered w-full"
                          rows="2"
                          placeholder=${m.admin_registration_rejection_reason_placeholder()}
                          .value=${rejectionReason}
                          @input=${(e: InputEvent) => setRejectionReason((e.target as HTMLTextAreaElement).value)}
                        ></textarea>
                        <div class="flex justify-end">
                          <button
                            class="btn btn-error btn-sm"
                            ?disabled=${processing || !rejectionReason.trim()}
                            @click=${handleReject}
                          >
                            ${m.admin_registration_confirm_reject()}
                          </button>
                        </div>
                      </div>
                    `
                  : nothing}
              `
            : nothing}
        </div>
      </div>
    `;
  },
);

export const AdminRegistrationList = stateful(() => {
  const registrationsQuery = useQuery(
    () =>
      subscribeCompanyRegistrations()({
        _id: 1,
        companyName: 1,
        contactFirstName: 1,
        contactLastName: 1,
        contactEmail: 1,
        contactPosition: 1,
        address: 1,
        sector: 1,
        website: 1,
        services: 1,
        openPositions: 1,
        participationReason: 1,
        status: 1,
        submittedAt: 1,
      }),
    [],
  );

  const byDate = (a: { submittedAt?: Date | null }, b: { submittedAt?: Date | null }) =>
    new Date(b.submittedAt ?? 0).getTime() - new Date(a.submittedAt ?? 0).getTime();

  const pending = (registrationsQuery.value ?? [])
    .filter(r => r.status === CompanyRegistrationStatus.PENDING)
    .sort(byDate);
  const processed = (registrationsQuery.value ?? [])
    .filter(r => r.status !== CompanyRegistrationStatus.PENDING)
    .sort(byDate);

  return html`
    <div>
      <h1 class="text-xl font-bold mb-6">${m.admin_registrations_title()}</h1>

      ${registrationsQuery.loading
        ? html`<div class="flex justify-center py-8">
            <span class="loading loading-spinner loading-lg"></span>
          </div>`
        : pending.length === 0
          ? html`<div class="text-center py-8 text-base-content/60">${m.admin_registration_no_pending()}</div>`
          : html`
              <div class="space-y-4">
                ${pending.map(reg => RegistrationCard({ reg }))}
              </div>
            `}

      ${processed.length > 0
        ? html`
            <div class="divider mt-8"></div>
            <h2 class="text-lg font-semibold mb-4 opacity-60">${m.admin_registration_archive()}</h2>
            <div class="space-y-4 opacity-70">
              ${processed.map(reg => RegistrationCard({ reg }))}
            </div>
          `
        : nothing}
    </div>
  `;
});
